import {
	type ClientPropertyArtDetailResponseDTO,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { propertyCarTitle } from "web_myplann/Portfolio/Helpers/propertyCarTitle";

type PropertyType =
	| ClientPropertyVehicleDetailResponseDTO
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO
	| ClientPropertyArtDetailResponseDTO;

const isVehicle = (
	prop: PropertyType,
): prop is ClientPropertyVehicleDetailResponseDTO => {
	return prop.type === ClientPropertyTypeEnum.AutoMoto;
};

const isArt = (
	prop: PropertyType,
): prop is ClientPropertyArtDetailResponseDTO => {
	return prop.type === ClientPropertyTypeEnum.Art;
};

const isProperty = (
	prop: PropertyType,
): prop is
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO => {
	return (
		prop.type === ClientPropertyTypeEnum.House ||
		prop.type === ClientPropertyTypeEnum.Flat ||
		prop.type === ClientPropertyTypeEnum.Cottage ||
		prop.type === ClientPropertyTypeEnum.Land
	);
};

const buildPropertyTitleVehicle = (
	property: ClientPropertyVehicleDetailResponseDTO,
	t: (text: string) => string,
) => {
	const title = propertyCarTitle(
		property.carBrand,
		property.carModel,
		property.carLicensePlate,
	);

	if (title) {
		return title;
	}

	return t(Resources.PortfolioDetail.Property.PropertyType[property.type]);
};

const buildPropertyTitleArt = (
	property: ClientPropertyArtDetailResponseDTO,
	t: (text: string) => string,
) => {
	if (property.name) {
		return property.name;
	}

	return `${t(Resources.PortfolioDetail.Property.PropertyType[property.type])} ${property.propertyType ? `(${property.propertyType})` : ""} `;
};

const buildPropertyTitleProperty = (
	property:
		| ClientPropertyHouseDetailResponseDTO
		| ClientPropertyFlatDetailResponseDTO
		| ClientPropertyCottageDetailResponseDTO
		| ClientPropertyLandDetailResponseDTO,
	t: (text: string) => string,
) => {
	if (property.name) {
		return property.name;
	}

	let location = "";

	const parts = [
		property.location,
		property.locationMunicipality,
		property.locationZipCode,
	].filter(Boolean);

	if (parts.length > 0) {
		location = parts.join(", ");
	}

	return `${t(Resources.PortfolioDetail.Property.PropertyType[property.type])} ${location}`;
};

export const getPropertyTitle = (
	property: PropertyType | undefined,
	t: (text: string) => string,
) => {
	if (!property) return "";

	if (isVehicle(property)) {
		return buildPropertyTitleVehicle(property, t);
	}

	if (isArt(property)) {
		return buildPropertyTitleArt(property, t);
	}

	if (isProperty(property)) {
		return buildPropertyTitleProperty(property, t);
	}

	return "";
};

export const usePropertyTitle = getPropertyTitle;
